import React, { MutableRefObject, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { Info } from '@mui/icons-material';
import { Nullable, SelectOption } from 'global';
import { zodResolver } from '@hookform/resolvers/zod';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { InputField, SelectField, CheckboxField } from 'components/form';
import { Box, Stack, Typography, Tooltip, Divider } from '@mui/material';
import { EquipmentCondition, PaymentType } from '@trnsact/trnsact-shared-types';

import { bpfAPI, Template } from '../../../api';
import { stagesMapper } from '../../../mappers';
import { TemplateFormValuesSchema } from '../../../model';
import { initialStagesConstructorFormValue } from '../../../constants';
import { extractApplyingForOptionsFromOcaDefinition } from '../../../lib';
import { TemplateConstructorMode, TemplateFormValues } from '../../../types';

interface Props {
  isDepartmentsLoading: boolean;
  template: Nullable<Template>;
  mode: TemplateConstructorMode;
  departmentsOptions: SelectOption[];
  submitRef: MutableRefObject<{ submit: () => Promise<TemplateFormValues | null> } | null>;
}

export const StagesConstructorForm = ({
  mode,
  template,
  submitRef,
  departmentsOptions,
  isDepartmentsLoading,
}: Props) => {
  const { control, getValues, trigger } = useForm<TemplateFormValues>({
    mode: 'onChange',
    resolver: zodResolver(TemplateFormValuesSchema),
    defaultValues: template ? stagesMapper.processTemplateToFormValues(template) : initialStagesConstructorFormValue,
  });

  const revalidateApplyRules = async () => {
    await trigger(['applyRules.paymentType', 'applyRules.applyingFor', 'applyRules.equipmentCondition']);
  };

  const dynamicsAccountId = useSelector<any, string | undefined>(state => state.account?.dynamicsAccountId);

  const { data: applyingForData, loading: isApplyingForLoading } = useQuery(bpfAPI.fetchApplyingForOptions, {
    skip: !dynamicsAccountId,
    variables: {
      dynamicsId: dynamicsAccountId,
    },
  });

  const activeOcaTemplate = applyingForData?.publicAccount?.vendorProfile?.ocaTemplate?.find(
    (ocaTemplate: { active?: boolean | null }) => ocaTemplate?.active
  );

  const applyingForOptions = extractApplyingForOptionsFromOcaDefinition(activeOcaTemplate?.jsonDefinition);

  useImperativeHandle(submitRef, () => ({
    submit: async () => {
      const isValid = await trigger();
      return isValid ? getValues() : null;
    },
  }));

  return (
    <Stack gap="1rem">
      <Typography fontWeight={500}>Common Info</Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
        <InputField
          control={control}
          name="templateName"
          label="Template Name"
          inputProps={{
            required: true,
            variant: 'outlined',
          }}
        />

        <SelectField
          required
          control={control}
          variant="outlined"
          label="Department"
          name="businessProcess"
          disabled={mode === 'edit'}
          options={departmentsOptions}
          isLoading={isDepartmentsLoading}
        />

        {mode !== 'edit' && <CheckboxField name="isActive" control={control} label="Make template active" />}
      </Box>

      <Divider />

      <Stack direction="row" alignItems="center" gap="0.5rem">
        <Typography fontWeight={500}>Apply Business Rules</Typography>

        <Tooltip
          arrow
          title={<Typography align="center">Template will attach to new deals based on this rule</Typography>}
        >
          <Info fontSize="small" color="primary" />
        </Tooltip>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
        <SelectField
          control={control}
          variant="outlined"
          label="Payment Type"
          name="applyRules.paymentType"
          extraAction={revalidateApplyRules}
          options={[{ label: 'None', value: '' }, ...convertEnumToSelectOptions(PaymentType)]}
          slotProps={{
            select: {
              displayEmpty: true,
            },
            inputLabel: {
              shrink: true,
            },
          }}
        />

        <SelectField
          control={control}
          variant="outlined"
          label="Applying For"
          name="applyRules.applyingFor"
          extraAction={revalidateApplyRules}
          isLoading={isApplyingForLoading}
          options={[{ label: 'None', value: '' }, ...applyingForOptions]}
          slotProps={{
            select: {
              displayEmpty: true,
            },
            inputLabel: {
              shrink: true,
            },
          }}
        />

        <SelectField
          control={control}
          variant="outlined"
          label="Equipment Condition"
          name="applyRules.equipmentCondition"
          extraAction={revalidateApplyRules}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          options={
            [
              { label: 'None', value: '' },
              { label: 'New', value: EquipmentCondition.New },
              { label: 'Used', value: EquipmentCondition.Used },
              { label: 'Reconditioned', value: EquipmentCondition.Reconditioned },
            ] as SelectOption[]
          }
        />
      </Box>
    </Stack>
  );
};
